// =======================
// sear/src/componentes/ListaAtos.js
// =======================

import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
  ButtonBase,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import RegistroAto from "./RegistraAto";
import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { api } from "@api/api";
import { formatarPrazoValidade } from "./comuns/prazoValidade";
import { ajustarTamanhoFonteAnalista } from "../estilos/global";

const ordenarAtosPorData = (atos) => {
  return [...atos].sort((a, b) => new Date(b.data) - new Date(a.data));
};

const estiloBotaoAzul = {
  backgroundColor: "#fff",
  borderColor: "primary.main",
  color: "primary.main",
  "&:hover": {
    backgroundColor: "primary.main",
    borderColor: "primary.main",
    color: "#fff",
  },
};

export default function ListaAtos() {
  const { id } = useParams();
  const perfil = window.location.pathname.split("/")[1];

  const [atos, setAtos] = useState([]);
  const [atoSelecionado, setAtoSelecionado] = useState(null);
  const atosOrdenados = ordenarAtosPorData(atos);
  const [loading, setLoading] = useState(true);
  const [atoParaEditar, setAtoParaEditar] = useState(null);
  const [atoParaExcluir, setAtoParaExcluir] = useState(null);
  const [confirmacaoExclusaoAberta, setConfirmacaoExclusaoAberta] =
    useState(false);

  const fetchAtos = useCallback(async () => {
    try {
      const response = await fetch(api.fetchActs(perfil, id), {
        credentials: "include",
      });

      const data = await response.json();
      const atosRecebidos = Array.isArray(data.data) ? data.data : [];
      const atosOrdenados = ordenarAtosPorData(atosRecebidos);

      setAtos(atosRecebidos);

      setAtoSelecionado((atoAtual) => {
        const atoAtualizado = atosOrdenados.find(
          (ato) => ato.id === atoAtual?.id,
        );

        return atoAtualizado || atosOrdenados[0] || null;
      });
    } catch (error) {
      console.error("Erro ao buscar atos:", error);
    } finally {
      setLoading(false);
    }
  }, [perfil, id]);

  useEffect(() => {
    fetchAtos();
  }, [fetchAtos]);

  const excluirAto = async (atoId) => {
    try {
      const response = await fetch(api.deleteAct(perfil, id, atoId), {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const erro = await response.json();
        throw new Error(erro.message || "Erro ao excluir ato.");
      }

      await fetchAtos();
      return true;
    } catch (error) {
      console.error("Erro ao excluir ato:", error);
      return false;
    }
  };

  const solicitarExclusao = (ato) => {
    setAtoParaExcluir(ato);
    setConfirmacaoExclusaoAberta(true);
  };

  const cancelarExclusao = () => {
    setConfirmacaoExclusaoAberta(false);
    setAtoParaExcluir(null);
  };

  const confirmarExclusao = async () => {
    if (!atoParaExcluir) return;

    const excluidoComSucesso = await excluirAto(atoParaExcluir.id);

    if (excluidoComSucesso) {
      setConfirmacaoExclusaoAberta(false);
      setAtoParaExcluir(null);
    }
  };

  const formatarDataSemFuso = (dataString) => {
    if (!dataString) return "";

    const [ano, mes, dia] = String(dataString).slice(0, 10).split("-");

    return `${dia}/${mes}/${ano}`;
  };

  const gerarPDF = () => {
    const doc = new jsPDF();
    const atosOrdenados = ordenarAtosPorData(atos);
    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - 2 * margin;
    const maxPageHeight = doc.internal.pageSize.getHeight() - margin;

    doc.setProperties({
      splitLongWords: true,
      lineBreakFactor: 0.9,
    });

    let yPosition = margin;

    atosOrdenados.forEach((ato) => {
      const startPage = doc.internal.getCurrentPageInfo().pageNumber;
      const startY = yPosition;

      const rectInfo = [];
      rectInfo.push({ page: startPage, startY: startY });

      doc.setFontSize(8);
      doc.setFont(undefined, "bold");

      const headerText = `IOF MG - Diário do Executivo - ${formatarDataSemFuso(
        ato.data,
      )} p.${ato.pagina} Col. ${ato.coluna}`;

      const headerLines = doc.splitTextToSize(headerText, contentWidth);

      if (yPosition + headerLines.length * 5 > maxPageHeight) {
        doc.addPage();
        yPosition = margin;

        rectInfo.push({
          page: doc.internal.getCurrentPageInfo().pageNumber,
          startY: yPosition,
        });
      }

      doc.text(headerLines, margin + 2, yPosition + 5);
      yPosition += headerLines.length * 5 + 3;

      doc.setFontSize(12);

      const titulo = `${ato.tipo} ${ato.orgao_emissor} Nº ${
        ato.numero_parecer_ou_portaria
      }${ato.retificacao ? " - Retificação" : ""}`;

      const tituloLines = doc.splitTextToSize(titulo, contentWidth);

      if (yPosition + tituloLines.length * 6 > maxPageHeight) {
        doc.addPage();
        yPosition = margin;

        rectInfo.push({
          page: doc.internal.getCurrentPageInfo().pageNumber,
          startY: yPosition,
        });
      }

      doc.text(
        tituloLines,
        margin +
          (contentWidth -
            (doc.getStringUnitWidth(titulo) * 12) / doc.internal.scaleFactor) /
            2,
        yPosition + 5,
      );

      yPosition += tituloLines.length * 6 + 5;

      if (ato.incluir_n_sei) {
        doc.setFontSize(10);

        const seiText = `Nº SEI: ${ato.incluir_n_sei}`;
        const seiLines = doc.splitTextToSize(seiText, contentWidth);

        if (yPosition + seiLines.length * 5 > maxPageHeight) {
          doc.addPage();
          yPosition = margin;

          rectInfo.push({
            page: doc.internal.getCurrentPageInfo().pageNumber,
            startY: yPosition,
          });
        }

        doc.text(
          seiLines,
          margin +
            (contentWidth -
              (doc.getStringUnitWidth(seiText) * 10) /
                doc.internal.scaleFactor) /
              2,
          yPosition + 5,
        );

        yPosition += seiLines.length * 5 + 5;
      }

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");

      const lines = doc.splitTextToSize(ato.corpo_do_texto, contentWidth - 4);

      for (let i = 0; i < lines.length; i++) {
        if (yPosition + 5 > maxPageHeight) {
          doc.addPage();
          yPosition = margin;

          rectInfo.push({
            page: doc.internal.getCurrentPageInfo().pageNumber,
            startY: yPosition,
          });
        }

        doc.text(lines[i], margin + 2, yPosition + 5);
        yPosition += 5;
      }

      yPosition += 5;

      if (ato.inicio_da_validade && ato.final_da_validade) {
        doc.setFontSize(8);

        const validadeText = `Validade: ${formatarDataSemFuso(
          ato.inicio_da_validade,
        )} - ${formatarDataSemFuso(
          ato.final_da_validade,
        )} - Prazo de validade: ${formatarPrazoValidade(
          ato.inicio_da_validade,
          ato.final_da_validade,
        )}`;

        const validadeLines = doc.splitTextToSize(validadeText, contentWidth);

        if (yPosition + validadeLines.length * 4 > maxPageHeight) {
          doc.addPage();
          yPosition = margin;

          rectInfo.push({
            page: doc.internal.getCurrentPageInfo().pageNumber,
            startY: yPosition,
          });
        }

        doc.text(validadeLines, margin + 2, yPosition + 5);
        yPosition += validadeLines.length * 4 + 2;
      }

      const uniquePages = [...new Set(rectInfo.map((info) => info.page))];
      const endY = yPosition;

      uniquePages.forEach((pageNum, i) => {
        doc.setPage(pageNum);

        const pageInfo = rectInfo.find((info) => info.page === pageNum);
        const rectYStart = pageInfo ? pageInfo.startY : margin;

        let rectYEnd;

        if (i === uniquePages.length - 1) {
          rectYEnd = endY;
        } else {
          rectYEnd = maxPageHeight;
        }

        doc.setDrawColor(0);
        doc.setLineWidth(0.5);

        doc.line(margin, rectYStart, margin, rectYEnd);

        doc.line(
          margin + contentWidth,
          rectYStart,
          margin + contentWidth,
          rectYEnd,
        );

        if (i === 0) {
          doc.line(margin, rectYStart, margin + contentWidth, rectYStart);
        }

        if (i === uniquePages.length - 1) {
          doc.line(margin, rectYEnd, margin + contentWidth, rectYEnd);
        }
      });

      if (yPosition + 15 > maxPageHeight) {
        doc.addPage();
        yPosition = margin;
      } else {
        yPosition += 15;
      }
    });

    doc.save("atos.pdf");
  };

  if (loading) return <CircularProgress />;

  const renderBotaoRegistrarAto = (sx = {}) => (
    <Button
      variant="outlined"
      color="primary"
      startIcon={<AddIcon />}
      onClick={() =>
        document.dispatchEvent(new CustomEvent("abrirDialogoRegistro"))
      }
      sx={{
        ...estiloBotaoAzul,
        ...sx,
      }}
    >
      Registrar Ato
    </Button>
  );

  const renderBotaoBaixarPDF = (sx = {}) => (
    <Button
      variant="outlined"
      color="primary"
      startIcon={<DescriptionOutlinedIcon />}
      onClick={gerarPDF}
      disabled={atos.length === 0}
      sx={{
        ...estiloBotaoAzul,
        ...sx,
      }}
    >
      Baixar PDF
    </Button>
  );

  const acoesAtos = (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        mb: 2,
      }}
    >
      {renderBotaoRegistrarAto({ flex: "1 1 150px" })}
      {renderBotaoBaixarPDF({ flex: "1 1 150px" })}
    </Box>
  );

  return (
    <Box>
      <RegistroAto
        onAtoRegistrado={fetchAtos}
        atoParaEditar={atoParaEditar}
        onEdicaoEncerrada={() => setAtoParaEditar(null)}
      />

      {atos.length === 0 ? (
        <Box
          sx={{
            p: 3,
            border: "1px solid #c9def4",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "420px", mx: "auto" }}>
            {acoesAtos}
          </Box>

          <Typography color="text.secondary">Nenhum ato encontrado.</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(260px, 0.75fr) minmax(0, 2fr)",
            },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {/* Lista lateral */}
          <Box
            sx={{
              p: 2,
              minWidth: 0,
              border: "1px solid #c9def4",
              borderRadius: "6px",
              backgroundColor: "#fff",
            }}
          >
            {renderBotaoRegistrarAto({ width: "100%" })}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                mt: 1.5,
                mb: 1.5,
                maxHeight: "310px",
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {atosOrdenados.map((ato) => {
                const selecionado = atoSelecionado?.id === ato.id;

                return (
                  <ButtonBase
                    key={ato.id}
                    onClick={() => setAtoSelecionado(ato)}
                    aria-pressed={selecionado}
                    sx={{
                      width: "100%",
                      display: "block",
                      p: 1.5,
                      textAlign: "left",
                      border: "1px solid #c9def4",
                      borderLeft: selecionado ? "4px solid" : "1px solid",
                      borderLeftColor: selecionado ? "primary.main" : "#c9def4",
                      borderRadius: "6px",
                      backgroundColor: selecionado ? "#eaf3fd" : "#fff",
                      "&:hover": {
                        backgroundColor: selecionado ? "#eaf3fd" : "#f7fbff",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                        lineHeight: 1.3,
                      }}
                    >
                      {formatarDataSemFuso(ato.data)} • {ato.orgao_emissor}
                    </Typography>

                    <Typography
                      sx={{
                        color: selecionado ? "primary.main" : "text.primary",
                        fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                        fontWeight: 500,
                        lineHeight: 1.35,
                        mt: 0.75,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {ato.tipo} {ato.orgao_emissor} nº{" "}
                      {ato.numero_parecer_ou_portaria}
                      {ato.retificacao ? " - Retificação" : ""}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>

            {renderBotaoBaixarPDF({ width: "100%" })}
          </Box>

          {/* Ato selecionado */}
          <Box
            sx={{
              minWidth: 0,
              height: "100%",
            }}
          >
            {atoSelecionado ? (
              <Paper
                elevation={0}
                sx={{
                  minHeight: "360px",
                  height: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #90caf9",
                  borderTop: "5px solid",
                  borderTopColor: "primary.main",
                  borderRadius: "6px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                {/* Cabeçalho do ato */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    p: { xs: 1.25, sm: 1.5 },
                    borderBottom: "1px solid #90caf9",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        borderRadius: "6px",
                        backgroundColor: "#eaf3fd",
                        color: "primary.main",
                      }}
                    >
                      <DescriptionOutlinedIcon />
                    </Box>

                    <Typography
                      sx={{
                        color: "text.primary",
                        fontSize: { xs: ajustarTamanhoFonteAnalista("0.95rem"), sm: ajustarTamanhoFonteAnalista("1.05rem") },
                        fontWeight: 500,
                        lineHeight: 1.35,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {atoSelecionado.tipo} {atoSelecionado.orgao_emissor} Nº{" "}
                      {atoSelecionado.numero_parecer_ou_portaria}
                      {atoSelecionado.retificacao ? " - Retificação" : ""}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Tooltip title="Editar Ato" arrow>
                      <IconButton
                        aria-label="Editar Ato"
                        onClick={() => setAtoParaEditar(atoSelecionado)}
                        sx={{
                          color: "primary.main",
                          backgroundColor: "#eaf3fd",
                          borderRadius: "6px",
                          "&:hover": {
                            color: "#fff",
                            backgroundColor: "primary.main",
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Excluir Ato" arrow>
                      <IconButton
                        aria-label="Excluir Ato"
                        onClick={() => solicitarExclusao(atoSelecionado)}
                        sx={{
                          color: "error.main",
                          backgroundColor: "#fdecec",
                          borderRadius: "6px",
                          "&:hover": {
                            color: "#fff",
                            backgroundColor: "error.main",
                          },
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Corpo do ato */}
                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography
                    sx={{
                      color: "text.primary",
                      fontSize: ajustarTamanhoFonteAnalista("0.8rem"),
                      fontWeight: 500,
                    }}
                  >
                    IOF MG - Diário do Executivo -{" "}
                    {formatarDataSemFuso(atoSelecionado.data)} p.
                    {atoSelecionado.pagina} Col. {atoSelecionado.coluna}
                  </Typography>

                  <Typography
                    align="center"
                    sx={{
                      color: "text.primary",
                      fontSize: ajustarTamanhoFonteAnalista("1.1rem"),
                      fontWeight: 500,
                      lineHeight: 1.4,
                      mt: 2,
                    }}
                  >
                    {atoSelecionado.tipo} {atoSelecionado.orgao_emissor} Nº{" "}
                    {atoSelecionado.numero_parecer_ou_portaria}
                    {atoSelecionado.retificacao ? " - Retificação" : ""}
                  </Typography>

                  {atoSelecionado.incluir_n_sei && (
                    <Typography
                      align="center"
                      sx={{
                        color: "text.primary",
                        fontSize: ajustarTamanhoFonteAnalista("0.9rem"),
                        fontWeight: 500,
                        mt: 1,
                      }}
                    >
                      Nº SEI: {atoSelecionado.incluir_n_sei}
                    </Typography>
                  )}

                  <Typography
                    align="justify"
                    sx={{
                      color: "text.primary",
                      fontSize: ajustarTamanhoFonteAnalista("0.9rem"),
                      lineHeight: 1.5,
                      mt: 2.5,
                      whiteSpace: "pre-line",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {atoSelecionado.corpo_do_texto}
                  </Typography>

                  {atoSelecionado.inicio_da_validade &&
                    atoSelecionado.final_da_validade && (
                      <Typography
                        sx={{
                          color: "text.primary",
                          fontSize: ajustarTamanhoFonteAnalista("0.8rem"),
                          mt: 4,
                        }}
                      >
                        Validade:{" "}
                        {formatarDataSemFuso(atoSelecionado.inicio_da_validade)}{" "}
                        -{" "}
                        {formatarDataSemFuso(atoSelecionado.final_da_validade)}{" "}
                        - Prazo de validade:{" "}
                        {formatarPrazoValidade(
                          atoSelecionado.inicio_da_validade,
                          atoSelecionado.final_da_validade,
                        )}
                      </Typography>
                    )}
                </Box>
              </Paper>
            ) : (
              <Typography color="text.secondary">
                Selecione um ato para visualizar.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Dialog
        open={confirmacaoExclusaoAberta}
        onClose={cancelarExclusao}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: "5px solid",
            borderColor: "error.main",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontWeight: "bold",
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "error.main",
              color: "white",
              fontSize: ajustarTamanhoFonteAnalista("1.4rem"),
              fontWeight: "bold",
            }}
          >
            !
          </Box>
          Atenção
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            Deseja realmente excluir este ato?
          </DialogContentText>

          <DialogContentText
            sx={{
              mt: 1,
              fontWeight: "bold",
              color: "error.main",
            }}
          >
            Esta ação não poderá ser desfeita.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={cancelarExclusao} variant="outlined" color="inherit">
            Não, voltar
          </Button>

          <Button
            onClick={confirmarExclusao}
            variant="contained"
            style={{
              backgroundColor: "#d32f2f",
              color: "#ffffff",
            }}
          >
            Sim, excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
